import FormBase from '@/components/forms/new/FormBase';
import { Box } from '@chakra-ui/react';
import ServiceProfileNameField from './formFields/ServiceProfileNameField';
import ServiceProfileVisibleScope from './formFields/ServiceProfileVisibleScope';
import DirectRoutingDialPlansField from './formFields/DirectRoutingDialPlansField';
import DirectRoutingRegionsField from './formFields/DirectRoutingRegionsField';
import DirectRoutingVoicePoliciesField from './formFields/DirectRoutingVoicePoliciesField';
import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import updateServiceProfiles from 'API/ServiceProfiles/updateServiceProfiles';
import { multiSelectDataMapper, toSentenceCase } from 'utils/utils';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import createServiceProfile from 'API/ServiceProfiles/createServiceProfile';
import ServiceProfileLoader from 'pages/services/serviceprofiles/ServiceProfileLoader';
import getGCCHServiceProfile from 'API/ServiceProfiles/getGCCHServiceProfile';
import getGCCHServiceProfileRegions from 'API/ServiceProfiles/getGCCHServiceProfileRegions';
import getGCCHServiceProfileDialPlans from 'API/ServiceProfiles/getGCCHServiceProfileDialPlans';
import getGCCHServiceProfileVoicePolicies from 'API/ServiceProfiles/getGCCHServiceProfileVoicePolicies';
import useFieldValidation from 'hooks/useFieldValidation';

import { validationSchemaDR } from './schema';

import BreadcrumbTrail from 'components/BreadcrumbTrail';
import useGetCurrentWholesaler from '@/queries/wholesaler/useGetCurrentWholesaler';
import { resolveWholesalerProfileName } from './serviceProfileName';

const GCCHighServiceProfile = ({
    mode,
    data,
    level,
    handleBackButton,
    visibleScopeOptions,
}) => {
    const [voicePolicyEnum, setVoicePolicyEnum] = useState([]);
    const [regionsEnum, setRegionsEnum] = useState([]);
    const [dialPlansEnum, setDialPlansEnum] = useState([]);
    const {
        currentWholesaler: wholesalerID,
        currentPartner: partnerID,
        companiesAndPartners,
    } = useSelector((state) => state.navigationLists);
    const { data: currentWholesalerData } = useGetCurrentWholesaler();
    const { requiredScope } = useSelector((state) => state.login);

    const { darkMode } = useSelector((state) => state.settings);

    const DEFAULT_GCCH_PAYLOAD = {
        name: resolveWholesalerProfileName(null, 'Direct Routing GCCH'),
        partnerID: level === 'partner' ? partnerID : undefined,
        wholesalerID: level === 'partner' ? undefined : wholesalerID,
        voicePolicies: [],
        dialPlans: [],
        regions: [],
        visibleScope: level === 'partner' ? 20 : 60,
    };

    useEffect(() => {
        if (
            mode !== 'EDIT' &&
            level !== 'partner' &&
            currentWholesalerData?.name
        ) {
            handleChangeState({
                name: resolveWholesalerProfileName(
                    currentWholesalerData?.name,
                    'Direct Routing GCCH',
                ),
            });
        } else if (
            mode !== 'EDIT' &&
            level === 'partner' &&
            companiesAndPartners?.length > 0
        ) {
            const partnerName = companiesAndPartners.find(
                ({ id }) => id === partnerID,
            )?.name;
            handleChangeState({
                name: `${partnerName} - Direct Routing GCCH Partner Profile`,
            });
        }
    }, [
        mode,
        companiesAndPartners,
        currentWholesalerData?.name,
        level,
        wholesalerID,
        partnerID,
    ]);

    const [modalState, setModalState] = useState(data || DEFAULT_GCCH_PAYLOAD);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    useFieldValidation(modalState, validationSchemaDR);

    const scope = useCallback(() => {
        if (level === 'partner') {
            return `Partner/${partnerID}`;
        } else {
            return `Wholesaler/${wholesalerID}`;
        }
    }, [level, partnerID, wholesalerID]);

    const handleChangeState = (payload) => {
        setModalState((prev) => ({
            ...prev,
            ...payload,
        }));
    };

    const handleGetVoicePolicies = useCallback(() => {
        getGCCHServiceProfileVoicePolicies({
            scope: scope(),
            regions: modalState.regions,
        })
            .then((res) => {
                setVoicePolicyEnum(res.data.map(multiSelectDataMapper));
            })
            .catch((e) => {
                setError(
                    e?.response?.data?.message ||
                        e?.message ||
                        'An error occurred loading voice policies.',
                );
            });
    }, [scope, modalState.regions]);

    const handleGetDropdown = useCallback(() => {
        setIsLoading(true);
        const fetchSPData = mode === 'EDIT' && data;
        Promise.all([
            fetchSPData
                ? getGCCHServiceProfile({ scope: scope() })
                : setTimeout(() => {
                      Promise.resolve();
                  }, 1),
            getGCCHServiceProfileRegions({ scope: scope() }),
            getGCCHServiceProfileDialPlans({ scope: scope() }),
        ])
            .then(([spResult, regionsResult, dialPlansResult]) => {
                setRegionsEnum(regionsResult.data.map(multiSelectDataMapper));
                setDialPlansEnum(
                    dialPlansResult.data.map(multiSelectDataMapper),
                );
                if (fetchSPData) {
                    const regions = spResult.data.regions.map(
                        multiSelectDataMapper,
                    );
                    const dialPlans = spResult.data.dialPlans.map(
                        multiSelectDataMapper,
                    );
                    const voicePolicies = spResult.data.voicePolicies.map(
                        multiSelectDataMapper,
                    );

                    setModalState({
                        ...spResult.data,
                        regions,
                        dialPlans,
                        voicePolicies,
                    });
                }
            })
            .catch((e) => {
                setError(
                    e?.response?.data?.message ||
                        e?.message ||
                        'An error occurred loading service profile data.',
                );
            })
            .finally(() => {
                setTimeout(() => {
                    setIsLoading(false);
                }, 500);
            });
    }, [mode, data, scope]);

    useEffect(() => {
        if (modalState.regions && modalState.regions.length > 0) {
            handleGetVoicePolicies();
        }
    }, [handleGetVoicePolicies, modalState.regions]);

    useEffect(() => {
        handleGetDropdown();
    }, [mode, data, level, handleGetDropdown]);

    if (isLoading) {
        return <ServiceProfileLoader />;
    }

    // Helper to map value(s) to option(s) for select fields
    const mapToOptions = (values, options) => {
        if (!values || !options) return [];
        if (Array.isArray(values)) {
            return (
                values
                    .map((v) => options.find((o) => o.value === (v.value ?? v)))
                    ?.filter(Boolean) || []
            );
        }
        return (
            options.find((o) => o.value === (values.value ?? values)) || null
        );
    };
    const initialValues = {
        name: modalState?.name || '',
        regions: mapToOptions(modalState?.regions, regionsEnum),
        dialPlans: mapToOptions(modalState?.dialPlans, dialPlansEnum),
        voicePolicies: mapToOptions(modalState?.voicePolicies, voicePolicyEnum),
        partnerID:
            level === 'partner'
                ? (modalState?.partnerID ?? partnerID)
                : undefined,
        wholesalerID:
            level === 'partner'
                ? undefined
                : (modalState?.wholesalerID ?? wholesalerID),
        visibleScope:
            modalState?.visibleScope ?? (level === 'partner' ? 20 : 60),
    };

    const finalFormSubmit = async (values, actions) => {
        setSubmitted(true);
        setError(null);
        setIsLoading(true);
        try {
            if (mode === 'EDIT') {
                await updateServiceProfiles({
                    type: 'DirectRoutingGCCH',
                    payload: modalState,
                    level,
                    scope: requiredScope,
                });
                handleBackButton();
            } else {
                await createServiceProfile({
                    type: 'DirectRoutingGCCH',
                    payload: modalState,
                    level,
                    scope: requiredScope,
                });
                handleBackButton();
            }

            handleBackButton();
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                    e?.message ||
                    'An error occurred. Please try again later.',
            );
        } finally {
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };
    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Configurations', path: '' },
    ];
    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`${toSentenceCase(mode)} ${modalState.name || data?.name}`}
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                ufBackBtn="gcchServiceProfileBackBtn"
                ufSaveBtn="gcchServiceProfileSaveBtn"
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                enableReinitialize={true}>
                {({ errors = {}, touched = {} }) =>
                    [
                        <ServiceProfileNameField
                            key="name"
                            darkMode={darkMode}
                            minWidth="420px"
                            maxWidth="520px"
                            width="100%"
                            errors={errors}
                            touched={touched}
                            onChange={(e) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                }))
                            }
                        />,
                        <ServiceProfileVisibleScope
                            key="visibleScope"
                            options={visibleScopeOptions}
                            onChange={(e) => {
                                setModalState((prev) => ({
                                    ...prev,
                                    visibleScope: e.value,
                                }));
                            }}
                            darkMode={darkMode}
                            minWidth="420px"
                            maxWidth="520px"
                            width="100%"
                            errors={errors}
                            touched={touched}
                        />,
                        <DirectRoutingDialPlansField
                            key="dialPlans"
                            value={modalState.dialPlans}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    dialPlans: val,
                                }))
                            }
                            options={dialPlansEnum}
                            darkMode={darkMode}
                            error={
                                !modalState.dialPlans ||
                                modalState.dialPlans.length === 0
                                    ? 'At least one dial plan is required'
                                    : ''
                            }
                            showError={submitted}
                            disableStackHeight={true}
                            errors={errors}
                            touched={touched}
                        />,
                        <DirectRoutingRegionsField
                            key="regions"
                            value={modalState.regions}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    regions: val,
                                }))
                            }
                            options={regionsEnum}
                            darkMode={darkMode}
                            error={
                                !modalState.regions ||
                                modalState.regions.length === 0
                                    ? 'At least one region is required'
                                    : ''
                            }
                            showError={submitted}
                            disableStackHeight={true}
                            errors={errors}
                            touched={touched}
                        />,
                        <DirectRoutingVoicePoliciesField
                            key="voicePolicies"
                            value={modalState.voicePolicies}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    voicePolicies: val,
                                }))
                            }
                            options={voicePolicyEnum}
                            darkMode={darkMode}
                            error={
                                !modalState.voicePolicies ||
                                modalState.voicePolicies.length === 0
                                    ? 'At least one voice policy is required'
                                    : ''
                            }
                            showError={submitted}
                            disableStackHeight={true}
                            errors={errors}
                            touched={touched}
                        />,
                        error && (
                            <Box paddingBottom="0.5rem" key="error-box-gcchigh">
                                <Alert
                                    key="error"
                                    status="error"
                                    mt={4}
                                    marginBottom="1rem">
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            </Box>
                        ),
                    ].filter(Boolean)
                }
            </FormBase>
        </>
    );
};

export default GCCHighServiceProfile;
